import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Tooltip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Business as BusinessIcon,
  Folder as FolderIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0,0,0,0.08)',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.main,
  },
}));

const CallRelevanceCard = ({ call }) => {
  const navigate = useNavigate();
  const [hoveredChip, setHoveredChip] = useState(null);

  const handleCardClick = () => {
    navigate(`/funding/call/${call.id}`);
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return 'Closed';
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil <= 7) return `${daysUntil} days`;
    return date.toLocaleDateString();
  };

  const getStatusConfig = (status) => {
    const configs = {
      open: { label: 'Open', color: 'success', icon: CheckCircleIcon },
      upcoming: { label: 'Upcoming', color: 'info', icon: ScheduleIcon },
      closed: { label: 'Closed', color: 'default', icon: LockIcon },
      draft: { label: 'Draft', color: 'warning', icon: ScheduleIcon },
    };
    return configs[status] || configs.draft;
  };

  // Get overlapping skill and interest names for tooltips
  const overlappingSkills = call.matchingSkills || [];
  const overlappingInterests = call.matchingInterests || [];

  return (
    <StyledCard onClick={handleCardClick}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <CampaignIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {call.code || call.title || 'Untitled Call'}
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              color="text.primary" 
              sx={{ 
                fontWeight: 500, 
                mb: .5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {call.name || call.title || 'No title available'}
            </Typography>
          </Box>
          
          {/* <IconButton 
            size="small" 
            sx={{ ml: 1 }}
            onClick={handleCardClick}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton> */}
        </Box>

        {/* Agency and Programme Info */}
        <Stack spacing={1} mb={2}>
          {call.agency_name && (
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {call.agency_name}
              </Typography>
                - 
              <Typography variant="body2" color="text.secondary">
                {call.program_name}
              </Typography>
            </Box>
          )}
          {call.programme_name && (
            <Box display="flex" alignItems="center" gap={1}>
              <FolderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {call.programme_name}
              </Typography>
            </Box>
          )}
        </Stack>


        {/* Match Chips with Hover Tooltips */}
        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          {call.skillMatches > 0 && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Matching Skills ({overlappingSkills.length}):
                  </Typography>
                  {overlappingSkills.length > 0 ? (
                    overlappingSkills.map((skill, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                        • {skill.name || skill}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="caption">No skill details available</Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
              onOpen={() => setHoveredChip('skills')}
              onClose={() => setHoveredChip(null)}
            >
              <Chip
                label={`${call.skillMatches} Skill${call.skillMatches > 1 ? 's' : ''}`}
                color="primary"
                size="small"
                sx={{
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  transform: hoveredChip === 'skills' ? 'scale(1.05)' : 'scale(1)',
                }}
              />
            </Tooltip>
          )}

          {call.interestMatches > 0 && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Matching Interests ({overlappingInterests.length}):
                  </Typography>
                  {overlappingInterests.length > 0 ? (
                    overlappingInterests.map((interest, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                        • {interest.name || interest.code || interest}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="caption">No interest details available</Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
              onOpen={() => setHoveredChip('interests')}
              onClose={() => setHoveredChip(null)}
            >
              <Chip
                label={`${call.interestMatches} Interest${call.interestMatches > 1 ? 's' : ''}`}
                color="secondary"
                size="small"
                sx={{
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  transform: hoveredChip === 'interests' ? 'scale(1.05)' : 'scale(1)',
                  color: '#fff',
                }}
              />
            </Tooltip>
          )}

          {/* Total Match Score */}
          {/* {call.matchScore > 0 && (
            <Chip
              label={`Score: ${call.matchScore}`}
              variant="outlined"
              size="small"
              sx={{ 
                fontWeight: 600,
                borderColor: 'success.main',
                color: 'success.main',
              }}
            />
          )} */}
        </Box>


        {/* Status and Deadline */}
        <Box display="flex" gap={1} mt={2} flexWrap="wrap" >
          {call.status && (() => {


            const statusConfig = getStatusConfig(call.status);
            const StatusIcon = statusConfig.icon;
            return (
              <Chip
                icon={<StatusIcon sx={{ fontSize: 16 }} />}
                label={statusConfig.label}
                size="small"
                color={statusConfig.color}
                sx={{ fontSize: '0.75rem', fontWeight: 500 }}
              />
            );
          })()}


          {call.closes_at && (
            <Chip
              label={`Deadline: ${formatDeadline(call.closes_at)}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
            

        </Box>

      </CardContent>
    </StyledCard>
  );
};

export default CallRelevanceCard;
